/**
 * Ayarlar ekranı — üç sekme.
 *
 *   1. **Ayarlar**      — şirket bilgileri, fatura kuralları, alarm
 *   2. **Bağlantılar**  — Shopify, e-posta (SMTP), SMS + bağlantı testi
 *   3. **Sistem**       — salt okunur ortam bilgisi
 *
 * Düzen kuralı (docs/13 bölüm 4): etiket sütunu sabit, alan sütunu sınırlı,
 * form ortalanmış. Geniş ekranda kartın 1900px'e yayılıp etiketle değerin
 * arasında uçurum oluşmasını engeller (bkz. components/ui/Form.tsx).
 *
 * Gizli alanlar (SMTP şifresi, SMS anahtarı) sunucudan **gelmez**; yalnızca
 * "tanımlı" bilgisi gelir. Boş bırakmak "değiştirme" demektir — kullanıcı
 * sadece portu değiştirdiğinde şifresi uçmasın.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Copy,
  Link2,
  Loader2,
  Minus,
  Plug,
  Save,
  Send,
  Server,
  ShieldQuestion,
  SlidersHorizontal,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  api,
  type AyarGorunumu,
  type Baglayici,
  type BaglayiciAlani,
  type WebhookKurulumSonucu,
} from '../lib/api';
import { useSearchParams } from 'react-router';
import { Kabuk } from '../components/Kabuk';
import { Kart, KartBasligi, KartGovdesi } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { ALAN_SINIFI } from '../components/ui/Girdi';
import { Form, FormSatiri, FormAltCubugu } from '../components/ui/Form';
import { Sekmeler, type SekmeTanimi } from '../components/ui/Sekmeler';
import { Anahtar } from '../components/ui/Anahtar';
import { HataKutusu, Yukleniyor } from '../components/Durumlar';
import { basariBildir, bilgiBildir, hataBildir } from '../lib/bildirim';
import { oranYaz, tarihSaatYaz, tarihYaz } from '../lib/format';
import { cn } from '../lib/cn';
import { disAdresGecerliMi, geriCagriAdresi } from '../../../shared/webhook-adresi';

type Sekme = 'ayarlar' | 'baglantilar' | 'sistem';

// ---------------------------------------------------------------------------
// Ortak parçalar
// ---------------------------------------------------------------------------

function Isaret({ acik, acikMetin, kapaliMetin }: { acik: boolean; acikMetin: string; kapaliMetin: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap',
        acik
          ? 'bg-[var(--state-ok-soft)] text-[var(--state-ok)]'
          : 'bg-[var(--state-done-soft)] text-[var(--state-done)]'
      )}
    >
      {acik ? <Check size={12} aria-hidden="true" /> : <Minus size={12} aria-hidden="true" />}
      {acik ? acikMetin : kapaliMetin}
    </span>
  );
}

const SEKMELER: readonly SekmeTanimi<Sekme>[] = [
  { key: 'ayarlar', etiket: 'Ayarlar', ikon: SlidersHorizontal },
  { key: 'baglantilar', etiket: 'Bağlantılar', ikon: Plug },
  { key: 'sistem', etiket: 'Sistem', ikon: Server },
];

// ---------------------------------------------------------------------------
// Sekme 1: Ayarlar
// ---------------------------------------------------------------------------

/**
 * "GİB'de kontrol et" — firmanın kendi VKN'si e-Fatura mükellefi mi?
 *
 * Sonuç bildirim penceresinde gösterilir; ayar DEĞİŞTİRİLMEZ. Uyuşmazlıkta
 * ne yapılması gerektiği tek cümleyle söylenir.
 */
function GibKontrolDugmesi({ secili }: { secili: boolean }) {
  const kontrol = useMutation({
    mutationFn: api.gibKontrol,
    onSuccess: (sonuc) => {
      const govde = `${sonuc.mesaj}\n\n${sonuc.oneri}`;
      if (sonuc.uyusuyor && sonuc.ayar === secili) basariBildir(govde, 'GİB kaydı ile uyuşuyor');
      else bilgiBildir(govde, 'GİB kaydı ile UYUŞMUYOR');
    },
    onError: hataBildir,
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Dugme
        gorunum="ikincil"
        boy="sm"
        ikon={<ShieldQuestion size={13} />}
        bekliyor={kontrol.isPending}
        onClick={() => kontrol.mutate()}
      >
        GİB’de kontrol et
      </Dugme>
      <span className="text-xs text-[var(--ink-faint)]">
        Kendi VKN’nizi GİB kaydında sorgular; seçimi değiştirmez.
      </span>
    </div>
  );
}

function AyarAlani({
  ayar,
  deger,
  onDegistir,
  duzenlenebilir,
}: {
  ayar: AyarGorunumu;
  deger: string;
  onDegistir: (yeni: string) => void;
  duzenlenebilir: boolean;
}) {
  const alanId = `ayar-${ayar.key}`;

  if (ayar.tip === 'boolean') {
    return (
      <div className="flex flex-col gap-2">
        <Anahtar
          id={alanId}
          disabled={!duzenlenebilir}
          checked={deger === 'true'}
          onChange={(acik) => onDegistir(acik ? 'true' : 'false')}
          etiket={
            ayar.key === 'fatura.saticiMukellef'
              ? deger === 'true'
                ? 'Kayıtlıyız — alıcıya göre e-Fatura / e-Arşiv'
                : 'Kayıtlı değiliz — tüm faturalar e-Arşiv'
              : deger === 'true'
                ? 'Açık'
                : 'Kapalı'
          }
        />
        {/*
          Kararı FİRMA verir; bu düğme yalnızca GİB kaydındaki gerçeği gösterir.
          Otomatik değiştirmez — kullanıcı "bunu benim seçmem lazım" dedi.
        */}
        {ayar.key === 'fatura.saticiMukellef' && <GibKontrolDugmesi secili={deger === 'true'} />}
      </div>
    );
  }

  if (ayar.tip === 'secim') {
    return (
      <select
        id={alanId}
        disabled={!duzenlenebilir}
        value={deger}
        onChange={(o) => onDegistir(o.target.value)}
        className={ALAN_SINIFI}
      >
        <option value="">— seçilmedi —</option>
        {(ayar.secenekler ?? []).map((s) => (
          <option key={s.deger} value={s.deger}>
            {s.etiket}
          </option>
        ))}
      </select>
    );
  }

  // Çok satırlı ayar (ör. kargo firmaları listesi): satır başına bir kayıt, textarea şart.
  if (ayar.tip === 'coksatir') {
    return (
      <textarea
        id={alanId}
        rows={4}
        disabled={!duzenlenebilir}
        value={deger}
        onChange={(o) => onDegistir(o.target.value)}
        placeholder={ayar.ornek ?? ''}
        className={cn(ALAN_SINIFI, 'min-h-24 resize-y leading-relaxed')}
      />
    );
  }

  const tip =
    ayar.tip === 'sayi'
      ? 'number'
      : ayar.tip === 'tarih'
        ? 'date'
        : ayar.tip === 'sifre'
          ? 'password'
          : 'text';

  return (
    <input
      id={alanId}
      type={tip}
      disabled={!duzenlenebilir}
      value={deger}
      onChange={(o) => onDegistir(o.target.value)}
      placeholder={ayar.secret && ayar.tanimli ? '•••••••• (dokunmayın, değişmez)' : (ayar.ornek ?? '')}
      autoComplete={ayar.secret ? 'new-password' : 'off'}
      {...(ayar.enAz !== null ? { min: ayar.enAz } : {})}
      {...(ayar.enCok !== null ? { max: ayar.enCok } : {})}
      className={cn(ALAN_SINIFI, ayar.secret && 'rakam')}
    />
  );
}

function AyarlarSekmesi() {
  const sorguIstemcisi = useQueryClient();
  const [taslak, setTaslak] = useState<Record<string, string>>({});

  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['ayarlar-duzenlenebilir'],
    queryFn: api.duzenlenebilirAyarlar,
  });

  // Sunucudan yeni veri gelince taslağı sıfırla (kaydetme sonrası).
  useEffect(() => {
    setTaslak({});
  }, [data]);

  const kaydet = useMutation({
    mutationFn: api.ayarlariKaydet,
    onSuccess: (sonuc) => {
      basariBildir(sonuc.message, 'Kaydedildi');
      setTaslak({});
      void sorguIstemcisi.invalidateQueries({ queryKey: ['ayarlar-duzenlenebilir'] });
      void sorguIstemcisi.invalidateQueries({ queryKey: ['ayarlar'] });
    },
    onError: hataBildir,
  });

  const degisenSayisi = Object.keys(taslak).length;

  if (isPending) return <Yukleniyor satir={6} />;
  if (error !== null || !data) {
    return <HataKutusu hata={error} tekrarDene={() => void refetch()} />;
  }

  const degerAl = (ayar: AyarGorunumu): string => taslak[ayar.key] ?? ayar.deger;

  return (
    <div className="flex flex-col gap-6">
      {!data.canEdit && (
        <div className="rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface-sunken)] p-3 text-sm text-[var(--ink-muted)]">
          Ayarları görüntüleyebiliyorsunuz ama değiştiremezsiniz. Değişiklik için “ayarları değiştirme”
          yetkisi gerekir.
        </div>
      )}

      {!data.sifrelemeHazir && data.canEditSecrets && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--state-wait)] bg-[var(--state-wait-soft)] p-3"
        >
          <span className="-mt-px shrink-0 text-[var(--state-wait)]" aria-hidden="true">
            <TriangleAlert size={16} strokeWidth={2} />
          </span>
          <p className="max-w-[65ch] text-sm text-[var(--ink)]">
            Şifreleme anahtarı tanımlı değil — gizli alanlar (şifre, API anahtarı) kaydedilemez. Sunucudaki{' '}
            <span className="rakam">.env</span> dosyasına en az 16 karakterlik{' '}
            <span className="rakam">SETTINGS_ENCRYPTION_KEY</span> ekleyin.
          </p>
        </div>
      )}

      {data.gruplar.map((grup) => {
        const grupAyarlari = data.ayarlar.filter((a) => a.grup === grup.key);
        if (grupAyarlari.length === 0) return null;

        // Kaynak notu KART düzeyinde, alan alan değil: ölçüldü, her alanın altında
        // aynı cümle 12 kez tekrarlanıyor ve formu okunmaz hâle getiriyordu.
        const envKaynakli = grupAyarlari.some((a) => !a.secret && a.kaynak === 'env');
        return (
          <Kart key={grup.key}>
            <KartBasligi
              baslik={grup.etiket}
              aciklama={
                envKaynakli
                  ? `${grup.aciklama} · Bazı değerler şu an sunucu dosyasından (.env) geliyor; buraya girdiğiniz değer öncelik alır.`
                  : grup.aciklama
              }
            />
            <KartGovdesi>
              <Form>
                {grupAyarlari.map((ayar) => {
                  const gizliVeYetkisiz = ayar.secret && !data.canEditSecrets;
                  return (
                    <FormSatiri
                      key={ayar.key}
                      etiket={ayar.etiket}
                      aciklama={ayar.aciklama}
                      htmlFor={`ayar-${ayar.key}`}
                      zorunlu={ayar.zorunlu}
                      ipucu={
                        ayar.secret ? (
                          <span className="flex flex-wrap items-center gap-2">
                            <Isaret acik={ayar.tanimli} acikMetin="tanımlı" kapaliMetin="tanımsız" />
                            {gizliVeYetkisiz ? (
                              <span className="text-[var(--ink-faint)]">
                                Bu alanı değiştirmek için ayrı yetki gerekir.
                              </span>
                            ) : (
                              <span className="text-[var(--ink-faint)]">
                                Boş bırakırsanız mevcut değer korunur.
                              </span>
                            )}
                          </span>
                        ) : undefined
                      }
                    >
                      <AyarAlani
                        ayar={ayar}
                        deger={degerAl(ayar)}
                        duzenlenebilir={data.canEdit && !gizliVeYetkisiz}
                        onDegistir={(yeni) => setTaslak((t) => ({ ...t, [ayar.key]: yeni }))}
                      />
                    </FormSatiri>
                  );
                })}
              </Form>
            </KartGovdesi>
          </Kart>
        );
      })}

      <FormAltCubugu gorunur={degisenSayisi > 0 && data.canEdit}>
        <Dugme
          gorunum="birincil"
          ikon={<Save size={15} />}
          bekliyor={kaydet.isPending}
          onClick={() => kaydet.mutate(taslak)}
        >
          Kaydet
        </Dugme>
        <Dugme gorunum="ikincil" onClick={() => setTaslak({})}>
          Değişiklikleri geri al
        </Dugme>
        <span className="rakam text-xs text-[var(--ink-faint)]">{degisenSayisi} alan değişti</span>
      </FormAltCubugu>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sekme 2: Bağlantılar
// ---------------------------------------------------------------------------

function BaglayiciKarti({
  baglayici,
  duzenlenebilir,
  gizliDuzenlenebilir,
  onKaydet,
  onTest,
  onTestGonder,
  onWebhookKur,
  webhookKuruluyor = false,
  webhookSonucu = null,
  kaydediliyor,
  testEdiliyor,
  gonderiliyor,
}: {
  baglayici: Baglayici;
  duzenlenebilir: boolean;
  gizliDuzenlenebilir: boolean;
  onKaydet: (govde: { provider: string; isEnabled: boolean; degerler: Record<string, string> }) => void;
  onTest: () => void;
  onTestGonder: (hedef: string) => void;
  /** Yalnızca Shopify: webhook'ları kur/doğrula. */
  onWebhookKur?: () => void;
  webhookKuruluyor?: boolean;
  webhookSonucu?: WebhookKurulumSonucu | null;
  kaydediliyor: boolean;
  testEdiliyor: boolean;
  gonderiliyor: boolean;
}) {
  const [provider, setProvider] = useState(baglayici.provider);
  const [acik, setAcik] = useState(baglayici.isEnabled);
  const [degerler, setDegerler] = useState<Record<string, string>>(baglayici.config);
  const [testHedefi, setTestHedefi] = useState('');

  // Sunucudan yeni veri gelince formu tazele.
  useEffect(() => {
    setProvider(baglayici.provider);
    setAcik(baglayici.isEnabled);
    setDegerler(baglayici.config);
  }, [baglayici]);

  const secili = baglayici.saglayicilar.find((s) => s.key === provider);
  const gizliTanimli = (alan: BaglayiciAlani): boolean => baglayici.tanimliGizliAlanlar.includes(alan.key);

  const degisti =
    provider !== baglayici.provider ||
    acik !== baglayici.isEnabled ||
    JSON.stringify(degerler) !== JSON.stringify(baglayici.config);

  return (
    <Kart id={`baglayici-${baglayici.key}`}>
      <KartBasligi
        baslik={baglayici.etiket}
        aciklama={baglayici.aciklama}
        eylemler={
          <div className="flex flex-wrap items-center gap-2">
            {baglayici.lastTestOk !== null && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
                  baglayici.lastTestOk
                    ? 'bg-[var(--state-ok-soft)] text-[var(--state-ok)]'
                    : 'bg-[var(--state-bad-soft)] text-[var(--state-bad)]'
                )}
                title={baglayici.lastTestMessage ?? undefined}
              >
                {baglayici.lastTestOk ? <Check size={12} /> : <X size={12} />}
                {baglayici.lastTestOk ? 'bağlantı çalışıyor' : 'bağlantı başarısız'}
              </span>
            )}
            <Isaret acik={baglayici.isEnabled} acikMetin="açık" kapaliMetin="kapalı" />
          </div>
        }
      />
      <KartGovdesi className="flex flex-col gap-4">
        {/* Bu bağlantı olmadan ne olmaz — kullanıcı riski bilsin. */}
        <p className="rounded-[var(--radius-sm)] bg-[var(--surface-sunken)] px-3 py-2 text-xs text-[var(--ink-muted)]">
          <span className="font-medium text-[var(--ink)]">Bu bağlantı yoksa:</span> {baglayici.olmazsaNeOlur}
        </p>

        <Form>
          {baglayici.saglayicilar.length > 1 && (
            <FormSatiri etiket="Sağlayıcı" htmlFor={`saglayici-${baglayici.key}`}>
              <select
                id={`saglayici-${baglayici.key}`}
                value={provider}
                disabled={!duzenlenebilir}
                onChange={(o) => {
                  setProvider(o.target.value);
                  setDegerler({});
                }}
                className={ALAN_SINIFI}
              >
                {baglayici.saglayicilar.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.etiket}
                  </option>
                ))}
              </select>
            </FormSatiri>
          )}

          {secili?.aciklama && <p className="py-2 text-xs text-[var(--ink-muted)]">{secili.aciklama}</p>}

          {(secili?.alanlar ?? []).map((alan) => {
            const gizliVeYetkisiz = alan.secret === true && !gizliDuzenlenebilir;
            const alanId = `${baglayici.key}-${alan.key}`;

            return (
              <FormSatiri
                key={alan.key}
                etiket={alan.etiket}
                aciklama={alan.aciklama ?? null}
                htmlFor={alanId}
                zorunlu={alan.zorunlu === true}
                ipucu={
                  alan.secret === true ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <Isaret acik={gizliTanimli(alan)} acikMetin="tanımlı" kapaliMetin="tanımsız" />
                      <span className="text-[var(--ink-faint)]">
                        {gizliVeYetkisiz
                          ? 'Değiştirmek için ayrı yetki gerekir.'
                          : 'Boş bırakırsanız mevcut değer korunur.'}
                      </span>
                    </span>
                  ) : undefined
                }
              >
                {alan.tip === 'secim' ? (
                  <select
                    id={alanId}
                    value={degerler[alan.key] ?? ''}
                    disabled={!duzenlenebilir}
                    onChange={(o) => setDegerler((d) => ({ ...d, [alan.key]: o.target.value }))}
                    className={ALAN_SINIFI}
                  >
                    <option value="">— seçilmedi —</option>
                    {(alan.secenekler ?? []).map((s) => (
                      <option key={s.deger} value={s.deger}>
                        {s.etiket}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={alanId}
                    type={alan.tip === 'sifre' ? 'password' : alan.tip === 'sayi' ? 'number' : 'text'}
                    value={degerler[alan.key] ?? ''}
                    disabled={!duzenlenebilir || gizliVeYetkisiz}
                    onChange={(o) => setDegerler((d) => ({ ...d, [alan.key]: o.target.value }))}
                    placeholder={
                      alan.secret === true && gizliTanimli(alan)
                        ? '•••••••• (dokunmayın, değişmez)'
                        : (alan.ornek ?? '')
                    }
                    autoComplete={alan.secret === true ? 'new-password' : 'off'}
                    className={cn(ALAN_SINIFI, alan.secret === true && 'rakam')}
                  />
                )}
              </FormSatiri>
            );
          })}

          <FormSatiri
            etiket="Bu bağlantıyı kullan"
            aciklama="Kapalıyken sistem bu servise hiç bağlanmaz"
            htmlFor={`acik-${baglayici.key}`}
          >
            <Anahtar
              id={`acik-${baglayici.key}`}
              checked={acik}
              disabled={!duzenlenebilir}
              onChange={setAcik}
              etiket={acik ? 'Açık' : 'Kapalı'}
            />
          </FormSatiri>
        </Form>

        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--line)] pt-4">
          <Dugme
            gorunum="birincil"
            ikon={<Save size={15} />}
            bekliyor={kaydediliyor}
            disabled={!duzenlenebilir || !degisti}
            onClick={() => onKaydet({ provider, isEnabled: acik, degerler })}
          >
            Kaydet
          </Dugme>
          <Dugme
            gorunum="ikincil"
            ikon={testEdiliyor ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
            bekliyor={testEdiliyor}
            onClick={onTest}
          >
            Bağlantıyı test et
          </Dugme>
          {baglayici.lastTestAt && (
            <span className="rakam text-xs text-[var(--ink-faint)]">
              son test: {tarihSaatYaz(baglayici.lastTestAt)}
            </span>
          )}
        </div>

        {baglayici.lastTestMessage && (
          <p
            className={cn(
              'rounded-[var(--radius-sm)] px-3 py-2 text-xs',
              baglayici.lastTestOk
                ? 'bg-[var(--state-ok-soft)] text-[var(--state-ok)]'
                : 'bg-[var(--state-bad-soft)] text-[var(--state-bad)]'
            )}
          >
            {baglayici.lastTestMessage}
          </p>
        )}

        {/*
          Gerçek test gönderimi.
          Yalnızca mail ve SMS için: "bağlantıyı test et" hiçbir şey göndermez,
          ama Netgsm'de güvenilir bir bakiye ucu yok — kimliği kesin doğrulamanın
          tek yolu gerçekten göndermek. Hedefi KULLANICI yazar; müşteriye asla
          gitmez. SMS ücretli olduğu için bu açıkça yazılır.
        */}
        {(baglayici.key === 'mail' || baglayici.key === 'sms') && duzenlenebilir && (
          <div className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface-sunken)] p-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-[var(--ink)]">
                Kendinize test {baglayici.key === 'sms' ? 'SMS’i' : 'e-postası'} gönderin
              </span>
              <span className="text-xs text-[var(--ink-muted)]">
                {baglayici.key === 'sms'
                  ? 'Ayarların gerçekten çalıştığını kanıtlayan tek yol. Bir SMS ücreti yansır. Müşteriye gitmez — numarayı siz yazarsınız.'
                  : 'Ayarların gerçekten çalıştığını kanıtlar. Müşteriye gitmez — adresi siz yazarsınız.'}
              </span>
            </div>
            <div className="flex flex-col gap-2 @xl:flex-row">
              <input
                type={baglayici.key === 'sms' ? 'tel' : 'email'}
                value={testHedefi}
                onChange={(o) => setTestHedefi(o.target.value)}
                placeholder={baglayici.key === 'sms' ? '0532 123 45 67' : 'siz@deercase.com'}
                className={cn(ALAN_SINIFI, '@xl:max-w-xs')}
              />
              <Dugme
                gorunum="ikincil"
                ikon={<Send size={15} />}
                bekliyor={gonderiliyor}
                disabled={testHedefi.trim().length < 3}
                onClick={() => onTestGonder(testHedefi.trim())}
              >
                {baglayici.key === 'sms' ? 'Test SMS gönder' : 'Test e-postası gönder'}
              </Dugme>
            </div>
          </div>
        )}

        {/*
          Shopify webhook kurulumu — "bağla"nın ikinci yarısı. Token girmek
          Shopify'ı BİZE bağlar; webhook, Shopify'ın BİZİ bulmasını sağlar.
          Kurulmadan tek sipariş bile gelmez. İdempotent: doğru olanlara dokunmaz.
        */}
        {baglayici.key === 'shopify' && (
          <div className="flex flex-col gap-4 rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface-sunken)] p-4">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-[var(--ink)]">Sipariş bildirimleri (webhook)</span>
              <span className="text-xs text-[var(--ink-muted)]">
                Shopify, ödenen siparişi ve iadeyi bu adrese bildirir. Bildirim gelmezse fatura kesilmez.
              </span>
            </div>

            {/* 1) Adres — kopyalanabilir. Yol ne olursa olsun Shopify'a bu yazılır. */}
            <WebhookAdresi disAdres={degerler['webhookAdresi'] ?? ''} />

            {/* 2) İki yol: otomatik (düğme) ya da elle (Shopify yönetim paneli) */}
            <div className="grid gap-4 @2xl:grid-cols-2">
              <div className="flex flex-col gap-2 rounded-[var(--radius-sm)] bg-[var(--surface)] p-3">
                <span className="text-xs font-semibold tracking-wide text-[var(--ink-muted)] uppercase">
                  Yol 1 — Otomatik (önerilen)
                </span>
                <span className="text-xs text-[var(--ink-muted)]">
                  Düğme, iki bildirimi Shopify’da bu adrese abone eder; kurulu olanı doğrular. Erişim
                  anahtarında “Webhook aboneliklerini yönet” izni gerekir. İmza anahtarı = uygulamanın{' '}
                  <strong className="font-medium text-[var(--ink)]">API gizli anahtarı</strong>.
                </span>
                {duzenlenebilir && onWebhookKur && (
                  <div>
                    <Dugme
                      gorunum="birincil"
                      boy="sm"
                      ikon={<Link2 size={14} />}
                      bekliyor={webhookKuruluyor}
                      disabled={degisti}
                      title={degisti ? 'Önce kaydedin' : undefined}
                      onClick={onWebhookKur}
                    >
                      Webhook’ları kur / doğrula
                    </Dugme>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 rounded-[var(--radius-sm)] bg-[var(--surface)] p-3">
                <span className="text-xs font-semibold tracking-wide text-[var(--ink-muted)] uppercase">
                  Yol 2 — Elle, Shopify panelinden
                </span>
                <ol className="flex list-decimal flex-col gap-1 pl-4 text-xs text-[var(--ink-muted)]">
                  <li>
                    Shopify → Ayarlar → Bildirimler →{' '}
                    <strong className="font-medium text-[var(--ink)]">Webhook’lar</strong> → “Webhook
                    oluştur”.
                  </li>
                  <li>
                    Olay <strong className="font-medium text-[var(--ink)]">Sipariş ödemesi</strong>{' '}
                    (orders/paid), biçim JSON, URL: yukarıdaki adres. Aynısını{' '}
                    <strong className="font-medium text-[var(--ink)]">Sipariş gönderimi</strong>{' '}
                    (orders/fulfilled) ve{' '}
                    <strong className="font-medium text-[var(--ink)]">İade oluşturma</strong> (refunds/create)
                    için tekrarlayın. Hangisinin fatura keseceğini Ayarlar → Fatura kuralları → “Fatura ne
                    zaman tetiklensin?” belirler.
                  </li>
                  <li>
                    Aynı sayfanın en altındaki{' '}
                    <strong className="font-medium text-[var(--ink)]">imza anahtarını</strong> yukarıdaki
                    “Webhook imza anahtarı” alanına yapıştırıp kaydedin.
                  </li>
                </ol>
              </div>
            </div>

            {webhookSonucu && (
              <div className="flex flex-col gap-2">
                <p
                  className={cn(
                    'rounded-[var(--radius-sm)] px-3 py-2 text-xs',
                    webhookSonucu.ok
                      ? 'bg-[var(--state-ok-soft)] text-[var(--state-ok)]'
                      : 'bg-[var(--state-bad-soft)] text-[var(--state-bad)]'
                  )}
                >
                  {webhookSonucu.mesaj}
                  {webhookSonucu.yapilacak && <> — {webhookSonucu.yapilacak}</>}
                </p>
                <ul className="flex flex-col gap-1 text-xs">
                  {webhookSonucu.gerekli.map((g) => {
                    const dogru =
                      webhookSonucu.dogru.some((d) => d.topic === g.topic) ||
                      webhookSonucu.kurulan.some((k) => k.topic === g.topic);
                    const yanlis = webhookSonucu.yanlisAdres.filter((y) => y.topic === g.topic);
                    return (
                      <li key={g.topic} className="flex flex-wrap items-baseline gap-2">
                        <Isaret acik={dogru} acikMetin="kayıtlı" kapaliMetin="eksik" />
                        <span className="rakam text-[var(--ink)]">{g.topic}</span>
                        <span className="text-[var(--ink-muted)]">{g.aciklama}</span>
                        {yanlis.length > 0 && (
                          <span className="text-[var(--state-bad)]">
                            başka adrese de gidiyor: {yanlis.map((y) => y.uri).join(', ')}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </KartGovdesi>
    </Kart>
  );
}

/** Webhook adresi — dış adresten türetilir, tek tıkla kopyalanır. Sunucuyla AYNI fonksiyon (shared/). */
function WebhookAdresi({ disAdres }: { disAdres: string }) {
  const gecerli = disAdres.trim() !== '' && disAdresGecerliMi(disAdres);
  const adres = gecerli ? geriCagriAdresi(disAdres) : null;

  const kopyala = async () => {
    if (!adres) return;
    try {
      await navigator.clipboard.writeText(adres);
      basariBildir('Webhook adresi kopyalandı — Shopify’a yapıştırın.');
    } catch {
      bilgiBildir(adres, 'Adresi elle kopyalayın');
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[0.8125rem] text-[var(--ink)]">Shopify’a tanıtılacak adres</span>
      {adres ? (
        <div className="flex flex-wrap items-center gap-2">
          <code className="rakam min-w-0 flex-1 rounded-[var(--radius-sm)] border border-[var(--line-strong)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--ink)] break-all select-all">
            {adres}
          </code>
          <Dugme gorunum="ikincil" boy="sm" ikon={<Copy size={14} />} onClick={() => void kopyala()}>
            Kopyala
          </Dugme>
        </div>
      ) : (
        <span className="text-xs text-[var(--state-bad)]">
          Önce yukarıda “Bu sunucunun dış adresi”ni <span className="rakam">https://</span> ile girip kaydedin
          — adres buradan türetilir.
        </span>
      )}
    </div>
  );
}

function BaglantilarSekmesi() {
  const sorguIstemcisi = useQueryClient();
  const [islemdeki, setIslemdeki] = useState<string | null>(null);

  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['baglayicilar'],
    queryFn: api.baglayicilar,
  });

  const tazele = () => void sorguIstemcisi.invalidateQueries({ queryKey: ['baglayicilar'] });

  const kaydet = useMutation({
    mutationFn: ({ key, govde }: { key: string; govde: Parameters<typeof api.baglayiciKaydet>[1] }) =>
      api.baglayiciKaydet(key, govde),
    onSuccess: (sonuc) => {
      basariBildir(sonuc.message);
      tazele();
    },
    onError: hataBildir,
    onSettled: () => setIslemdeki(null),
  });

  const testGonder = useMutation({
    mutationFn: ({ key, hedef }: { key: string; hedef: string }) => api.testGonderimi(key, hedef),
    onSuccess: (sonuc) => {
      if (sonuc.ok) basariBildir(sonuc.message, 'Gönderildi');
      else hataBildir(Object.assign(new Error(sonuc.message), { yapilacak: sonuc.action }));
      tazele();
    },
    onError: hataBildir,
    onSettled: () => setIslemdeki(null),
  });

  const [webhookSonucu, setWebhookSonucu] = useState<WebhookKurulumSonucu | null>(null);
  const webhookKur = useMutation({
    mutationFn: api.shopifyWebhookKur,
    onSuccess: (sonuc) => {
      setWebhookSonucu(sonuc);
      if (sonuc.ok) basariBildir(sonuc.mesaj, 'Webhook’lar hazır');
      else bilgiBildir(`${sonuc.mesaj}${sonuc.yapilacak ? ` — ${sonuc.yapilacak}` : ''}`, 'Webhook kurulumu');
      tazele();
    },
    onError: hataBildir,
    onSettled: () => setIslemdeki(null),
  });

  const test = useMutation({
    mutationFn: api.baglantiTest,
    onSuccess: (sonuc) => {
      if (sonuc.ok) basariBildir(sonuc.message, 'Bağlantı çalışıyor');
      else hataBildir(Object.assign(new Error(sonuc.message), { yapilacak: sonuc.action }));
      tazele();
    },
    onError: hataBildir,
    onSettled: () => setIslemdeki(null),
  });

  if (isPending) return <Yukleniyor satir={6} />;
  if (error !== null || !data) {
    return <HataKutusu hata={error} tekrarDene={() => void refetch()} />;
  }

  return (
    <div className="flex flex-col gap-6">
      {!data.sifrelemeHazir && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--state-wait)] bg-[var(--state-wait-soft)] p-3"
        >
          <span className="-mt-px shrink-0 text-[var(--state-wait)]" aria-hidden="true">
            <TriangleAlert size={16} strokeWidth={2} />
          </span>
          <p className="max-w-[65ch] text-sm text-[var(--ink)]">
            Şifreleme anahtarı tanımlı değil — şifre ve API anahtarları kaydedilemez. Sunucudaki{' '}
            <span className="rakam">.env</span> dosyasına{' '}
            <span className="rakam">SETTINGS_ENCRYPTION_KEY</span> ekleyin.
          </p>
        </div>
      )}

      {data.items.map((baglayici) => (
        <BaglayiciKarti
          key={baglayici.key}
          baglayici={baglayici}
          duzenlenebilir={data.canEdit}
          gizliDuzenlenebilir={data.canEditSecrets}
          kaydediliyor={islemdeki === `kaydet:${baglayici.key}`}
          testEdiliyor={islemdeki === `test:${baglayici.key}`}
          gonderiliyor={islemdeki === `gonder:${baglayici.key}`}
          onTestGonder={(hedef) => {
            setIslemdeki(`gonder:${baglayici.key}`);
            testGonder.mutate({ key: baglayici.key, hedef });
          }}
          onKaydet={(govde) => {
            setIslemdeki(`kaydet:${baglayici.key}`);
            kaydet.mutate({ key: baglayici.key, govde });
          }}
          onTest={() => {
            setIslemdeki(`test:${baglayici.key}`);
            test.mutate(baglayici.key);
          }}
          {...(baglayici.key === 'shopify'
            ? {
                onWebhookKur: () => {
                  setIslemdeki('webhook:shopify');
                  webhookKur.mutate();
                },
                webhookKuruluyor: islemdeki === 'webhook:shopify',
                webhookSonucu,
              }
            : {})}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sekme 3: Sistem (salt okunur)
// ---------------------------------------------------------------------------

function SistemSekmesi() {
  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['ayarlar'],
    queryFn: api.ayarlar,
    staleTime: 60_000,
  });

  if (isPending) return <Yukleniyor satir={5} />;
  if (error !== null || !data) {
    return <HataKutusu hata={error} tekrarDene={() => void refetch()} />;
  }

  const satir = (etiket: string, deger: string | number | null, rakam = false) => (
    <FormSatiri key={etiket} etiket={etiket}>
      <span className={cn('block py-2 text-sm break-words text-[var(--ink)]', rakam && 'rakam')}>
        {deger === null || deger === '' ? (
          <span className="text-[var(--ink-faint)]">tanımlı değil</span>
        ) : (
          deger
        )}
      </span>
    </FormSatiri>
  );

  return (
    <div className="flex flex-col gap-6">
      {data.supplier.missingFields.length > 0 && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--state-wait)] bg-[var(--state-wait-soft)] p-3"
        >
          <span className="-mt-px shrink-0 text-[var(--state-wait)]" aria-hidden="true">
            <TriangleAlert size={16} strokeWidth={2} />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <p className="max-w-[65ch] text-sm text-[var(--ink)]">
              Canlıya geçmeden doldurulması gereken alanlar var. GİB tarafında zorunludurlar; eksikken canlı
              ortamda sistem açılmaz.
            </p>
            <span className="rakam text-xs text-[var(--ink-muted)]">
              {data.supplier.missingFields.join(', ')}
            </span>
          </div>
        </div>
      )}

      <Kart>
        <KartBasligi
          baslik="Ortam"
          aciklama="Faturaların hangi Veriban ortamına gittiği — yalnızca sunucudan değişir"
          eylemler={
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-semibold',
                data.environment.isLive
                  ? 'bg-[var(--live-warning)] text-[var(--live-warning-ink)]'
                  : 'bg-[var(--state-done-soft)] text-[var(--state-done)]'
              )}
            >
              {data.environment.isLive ? 'CANLI' : 'TEST'}
            </span>
          }
        />
        <KartGovdesi>
          <Form>
            {satir('Veriban kullanıcısı', data.environment.veribanUser, true)}
            {satir('Fatura seri kodu', data.environment.serieCode, true)}
            {satir('e-Arşiv servisi', data.environment.earchiveUrl)}
            {satir('e-Fatura servisi', data.environment.einvoiceUrl)}
            {satir('Vergi kimlik no', data.supplier.vkn, true)}
            {satir('Ünvan', data.supplier.title)}
          </Form>
        </KartGovdesi>
      </Kart>

      <Kart>
        <KartBasligi baslik="Arka plan işleyicisi" aciklama="Faturaları gönderen süreç" />
        <KartGovdesi>
          <Form>
            {satir('Eşzamanlı iş sayısı', data.worker.concurrency, true)}
            {satir('En fazla deneme', data.worker.maxAttempts, true)}
            {satir('Durum sorgu aralığı', `${data.worker.statusPollIntervalMin} dakika`, true)}
            {satir('Belge arşivi', data.invoice.archivePath, true)}
            {satir('Varsayılan KDV', oranYaz(String(data.invoice.defaultVatRate)), true)}
            {satir(
              'Fatura başlangıç tarihi',
              data.invoice.startDate ? tarihYaz(data.invoice.startDate) : null,
              true
            )}
          </Form>
        </KartGovdesi>
      </Kart>

      <Kart>
        <KartBasligi baslik="Panel güvenliği" />
        <KartGovdesi>
          <Form>
            <FormSatiri etiket="Oturum süresi">
              <span className="rakam block py-2 text-sm text-[var(--ink)]">
                {data.security.sessionHours} saat
              </span>
            </FormSatiri>
            <FormSatiri etiket="Çerez yalnızca HTTPS" aciklama="Canlıda açık olmalı">
              <span className="block py-2">
                <Isaret acik={data.security.cookieSecure} acikMetin="açık" kapaliMetin="kapalı" />
              </span>
            </FormSatiri>
            <FormSatiri
              etiket="Giriş ekranı otomatik doldurma"
              aciklama="Yalnızca test; canlıda açıksa sistem başlamaz"
            >
              <span className="block py-2">
                <Isaret acik={data.security.devAutofill} acikMetin="açık" kapaliMetin="kapalı" />
              </span>
            </FormSatiri>
            <FormSatiri etiket="Google reCAPTCHA v3">
              <span className="block py-2">
                <Isaret
                  acik={data.security.recaptchaEnabled}
                  acikMetin="etkin"
                  kapaliMetin="yapılandırılmamış"
                />
              </span>
            </FormSatiri>
          </Form>
        </KartGovdesi>
      </Kart>

      <div className="rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface)] p-4 text-sm text-[var(--ink-muted)]">
        <p className="max-w-[65ch]">
          Bu sekme <strong className="font-medium text-[var(--ink)]">salt okunurdur</strong>. Veriban erişimi,
          veritabanı adresi ve test/canlı ayrımı bilinçli olarak sunucudaki{' '}
          <span className="rakam">.env</span> dosyasında tutulur — panelden değiştirilebilir olsalardı, panel
          ele geçirildiğinde şirket adına gerçek fatura kesilebilirdi.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Adres çubuğundaki `?sekme=` değeri geçerli bir sekme mi? */
function sekmeCoz(ham: string | null): Sekme {
  return ham === 'baglantilar' || ham === 'sistem' ? ham : 'ayarlar';
}

export function Ayarlar() {
  /*
   * Sekme adres çubuğunda: Kurulum ekranındaki "Düzelt" bağlantıları doğrudan
   * ilgili sekmeyi açabilsin (`/ayarlar?sekme=baglantilar#veriban`). Sekmeyi
   * yalnızca state'te tutsaydık her bağlantı ilk sekmeye düşerdi.
   */
  const [parametreler, setParametreler] = useSearchParams();
  const sekme = sekmeCoz(parametreler.get('sekme'));
  const setSekme = (yeni: Sekme) => {
    const p = new URLSearchParams(parametreler);
    if (yeni === 'ayarlar') p.delete('sekme');
    else p.set('sekme', yeni);
    setParametreler(p);
  };

  // #veriban gibi bir çapa varsa ilgili karta kaydır — sekme çizildikten sonra.
  useEffect(() => {
    const hedef = window.location.hash.slice(1);
    if (!hedef) return;
    const zamanlayici = window.setTimeout(() => {
      document.getElementById(`baglayici-${hedef}`)?.scrollIntoView({ block: 'start' });
    }, 50);
    return () => window.clearTimeout(zamanlayici);
  }, [sekme]);

  const baslik = useMemo(() => {
    if (sekme === 'baglantilar') return 'Dış servis bağlantıları ve test';
    if (sekme === 'sistem') return 'Sunucudan gelen ayarlar — salt okunur';
    return 'Şirket bilgileri, fatura kuralları ve bildirim';
  }, [sekme]);

  return (
    <Kabuk baslik="Ayarlar" altBaslik={baslik}>
      <Sekmeler sekmeler={SEKMELER} secili={sekme} onSec={setSekme} etiket="Ayarlar bölümleri" />

      {sekme === 'ayarlar' && <AyarlarSekmesi />}
      {sekme === 'baglantilar' && <BaglantilarSekmesi />}
      {sekme === 'sistem' && <SistemSekmesi />}
    </Kabuk>
  );
}
